import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { useCategories } from '../hooks/useCategories.js';
import { buildCategoryTree } from '../lib/buildCategoryTree.js';

/**
 * Normalise un texte pour la recherche : lowercase + retire les accents.
 */
function normalize(str) {
  return (str ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Aplatit l'arbre de catégories en une liste d'items enrichis (path, level, ancestors).
 */
function flattenTree(nodes, ancestors = []) {
  const result = [];
  for (const node of nodes) {
    const path = [...ancestors, node.name];
    result.push({ ...node, path, level: ancestors.length });
    if (node.children?.length) {
      result.push(...flattenTree(node.children, path));
    }
  }
  return result;
}

export default function CategoryCombobox({
  value,
  onChange,
  placeholder = 'Catégorie…',
  categories: categoriesProp,
}) {
  const { data, isLoading } = useCategories();
  const rawCategories = categoriesProp
    ?? (Array.isArray(data) ? data : data?.categories ?? data?.data ?? []);

  const flatItems = useMemo(() => {
    const tree = buildCategoryTree(rawCategories);
    return flattenTree(tree);
  }, [rawCategories]);

  const selectedItem = useMemo(
    () => flatItems.find((c) => c.id === value) ?? null,
    [flatItems, value]
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return flatItems;
    return flatItems.filter((item) => {
      const leafMatch = normalize(item.name).includes(q);
      const pathMatch = item.path.some((seg) => normalize(seg).includes(q));
      return leafMatch || pathMatch;
    });
  }, [flatItems, query]);

  // Keep highlighted in bounds
  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.children[highlighted];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [highlighted]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function openDropdown() {
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function select(item) {
    onChange(item?.id ?? null);
    setOpen(false);
    setQuery('');
  }

  function clear(e) {
    e.stopPropagation();
    onChange(null);
  }

  const handleKeyDown = useCallback(
    (e) => {
      if (!open) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlighted((h) => Math.max(h - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filtered[highlighted]) select(filtered[highlighted]);
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
        case 'Tab':
          setOpen(false);
          break;
      }
    },
    [open, filtered, highlighted] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const INDENT = ['pl-0', 'pl-4', 'pl-8'];
  const indentClass = (level) => INDENT[Math.min(level, 2)];

  const triggerLabel = selectedItem
    ? selectedItem.path.join(' › ')
    : placeholder;

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Trigger button */}
      <button
        type="button"
        onClick={openDropdown}
        className="flex w-full items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-left shadow-sm hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={isLoading}
      >
        <span className={`flex-1 truncate ${selectedItem ? 'text-slate-900' : 'text-slate-400'}`}>
          {isLoading ? 'Chargement…' : triggerLabel}
        </span>
        {selectedItem && (
          <span
            role="button"
            aria-label="Effacer la sélection"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') clear(e); }}
            className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-400"
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={14} className="shrink-0 text-slate-400" aria-hidden />
      </button>

      {/* Dropdown — min-width pour afficher le chemin hiérarchique complet sans troncature */}
      {open && (
        <div className="absolute left-0 z-50 mt-1 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden w-full min-w-[320px] max-w-[min(520px,calc(100vw-2rem))]">
          {/* Search header */}
          <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
            <Search size={14} className="text-slate-400 shrink-0" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher…"
              className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
              aria-label="Rechercher une catégorie"
              autoComplete="off"
            />
          </div>

          {/* Options list */}
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Catégories"
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-slate-400 text-center">
                Aucun résultat
              </li>
            ) : (
              filtered.map((item, idx) => {
                const isHighlighted = idx === highlighted;
                const isSelected = item.id === value;
                // Chemin parent (tout sauf la leaf elle-même)
                const parentPath = item.path.slice(0, -1).join(' › ');
                return (
                  <li
                    key={item.id}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setHighlighted(idx)}
                    onClick={() => select(item)}
                    className={`
                      flex flex-col cursor-pointer px-3 py-1.5 text-sm border-l-2
                      ${isHighlighted ? 'bg-slate-100 border-l-slate-400' : 'border-l-transparent'}
                      ${isSelected ? 'text-slate-900' : 'text-slate-700'}
                    `}
                  >
                    {parentPath && (
                      <span className="text-[10px] text-slate-400 truncate leading-tight">
                        {parentPath}
                      </span>
                    )}
                    <div className="flex items-center">
                      <span className={item.level === 0 ? 'font-semibold' : 'font-medium'}>
                        {item.name}
                      </span>
                      {isSelected && (
                        <span className="ml-auto text-slate-400 text-xs" aria-hidden>✓</span>
                      )}
                    </div>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
